import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { PermissionContext } from "../../contexts/PermissionContext";
import { useClientConfig } from "../../contexts/ClientConfigContext";
import { useTheme } from "@hooks/useTheme";
import { menuItems } from "../../router/sideMap";
import { getHighestRole } from "../../utils/common";
import ErrorDetailsModal from "@components/UI/ErrorDetailModal";
import { registerErrorModalOpener } from "@apis/errorModalApi";

const pathToSidebarNavTestId = (path) => {
  if (!path) return null;
  const normalized = path.replace(/^\//, "").replace(/\//g, "-");
  return `app-sidebar-nav-${normalized}`;
};

const parentMenuTestId = (item) =>
  item.path ? pathToSidebarNavTestId(item.path) : `app-sidebar-nav-group-${item.id}`;

const filterRoutes = (routes, loginPermissions) => {
  const highestPermission = getHighestRole(loginPermissions || []);

  // Step 1: Filter parent menu
  const filteredRoutes = routes.filter((route) => {
    // Hide parent menu if restrictedPermissions contains highestPermission
    if (route.restrictedPermissions?.includes(highestPermission)) {
      return false;
    }

    // Show parent menu if no permission
    if (!route.permission) {
      return true;
    }

    // Check permissions for parent menu (Apply for new feature)
    const hasPermission = Array.isArray(route.permission)
      ? route.permission.some((perm) => loginPermissions.includes(perm))
      : loginPermissions.includes(route.permission);
    return hasPermission;
  });

  // Step 2: Filter child menus for valid parent menus
  return filteredRoutes.map((route) => {
    if (!route.children) return route;

    return {
      ...route,
      children: route.children.filter((child) => {
        // Show submenu if no permission
        if (!child.permission) {
          return true;
        }

        // Check permission for submenu
        const hasPermission = Array.isArray(child.permission)
          ? child.permission.some((perm) => loginPermissions.includes(perm))
          : loginPermissions.includes(child.permission);
        return hasPermission;
      }),
    };
  });
};

const Sidebar = () => {
  const { t } = useTranslation();
  const { isDark, setTheme, colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions, shouldHideSystemMenu } = useContext(PermissionContext);

  // Safely handle useClientConfig hook to prevent crashes
  const clientConfigContext = useClientConfig();
  const { getClientConfigValue = () => null } = clientConfigContext || {};
  const [expandedSections, setExpandedSections] = useState({
    project: false,
    opensources: false,
    status: false,
    policy: false,
    bulletin: false,
    system: false,
  });
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  
  useEffect(() => {
    registerErrorModalOpener((errorList) => {
      setErrors(errorList);
      setOpen(true);
    });
  }, []);

  // Filter menu items based on user permissions and add active state
  const filteredMenuItems = useMemo(() => {
    let filtered = filterRoutes(menuItems, permissions);

    // Hide System menu if user only has DEVELOPER or USER permission without other permissions
    if (shouldHideSystemMenu) {
      filtered = filtered.filter(item => item.id !== "system");
    }

    // Then, apply client-config-based filtering without affecting the original logic
    const filteredWithClientConfig = filtered.map((item) => {
      if (item.id === "project" && item.children) {
        return {
          ...item,
          children: item.children.filter((child) => {
            // Only apply client-config filtering if context is available
            if (!clientConfigContext) {
              return true;
            }

            // Hide "Previews List" if usePreview is "N"
            const usePreview = getClientConfigValue("usePreview");
            if (child.name === "Previews List" && usePreview?.toLowerCase() === "n") {
              return false;
            }

            // Hide "Imported Component List" if useImportComponent is "false"
            const useImportComponent = getClientConfigValue("useImportComponent");
            if (child.name === "NEXUS Import" && useImportComponent?.toLowerCase() === "n") {
              return false;
            }

            // if (child.name === "NEXUS Import") {
            //   return false;
            // }

            return true;
          }),
        };
      }
      return item;
    });

    // Add active state based on current location
    return filteredWithClientConfig.map((item) => {
      // Helper function to check if menu path matches current path using segment matching
      const matchesCurrentPath = (menuPath) => {
        if (!menuPath) return false;
        const currentPathSegments = location.pathname.split("/").filter(Boolean);
        const menuPathSegments = menuPath.split("/").filter(Boolean);
        return menuPathSegments.every((segment, index) => currentPathSegments[index] === segment);
      };

      const isParentActive =
        // For menu without children, use segment matching
        (!item.children && item.path && matchesCurrentPath(item.path)) ||
        // For menu with children, check if any child matches
        (item.children &&
          item.children.some((child) => {
            // Special case: Handle /component-cve-status route for CVE Status menu item
            if (child.name === "CVE Status" && location.pathname.startsWith("/component-cve-status")) {
              return true;
            }

            // Check if current path starts with child path segments exactly
            return matchesCurrentPath(child.path);
          }));

      const childrenWithActive = item.children
        ? item.children.map((child) => {
            // Special case: Handle /component-cve-status route for CVE Status menu item
            if (child.name === "CVE Status" && location.pathname.startsWith("/component-cve-status")) {
              return {
                ...child,
                isActive: true,
              };
            }

            return {
              ...child,
              isActive: matchesCurrentPath(child.path),
            };
          })
        : undefined;

      return {
        ...item,
        isActive: isParentActive,
        children: childrenWithActive,
      };
    });
  }, [permissions, location.pathname, getClientConfigValue, clientConfigContext, shouldHideSystemMenu]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleMenuClick = (item, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // If sidebar is collapsed and item has children, navigate to first child
    if (isCollapsed && item.children && Array.isArray(item.children) && item.children.length > 0) {
      // Find the first child with a valid path
      const firstChildWithPath = item.children.find((child) => child && child.path);
      if (firstChildWithPath?.path) {
        navigate(firstChildWithPath.path);
        return;
      }
    }
    
    // Default behavior: toggle section if has children, or navigate if has path
    if (item.children && item.children.length > 0) {
      toggleSection(item.id);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // Auto-expand parent section when a child is active
  useEffect(() => {
    const activeParent = filteredMenuItems.find(
      (item) => item.children && item.children.some((child) => child.isActive),
    );

    if (activeParent && !expandedSections[activeParent.id]) {
      setExpandedSections((prev) => ({
        ...prev,
        [activeParent.id]: true,
      }));
    }
  }, [location.pathname, filteredMenuItems]);

  const handleSubmenuClick = (child) => {
    if (child.path) {
      navigate(child.path);
    }
  };

  return (
    <div
      data-testid="app-sidebar"
      className={`${isCollapsed ? "w-16" : "w-64"} flex h-full flex-col overflow-y-auto ${colors.sidebarBackground} ${colors.sidebarTextPrimary} transition-all duration-300`}
    >
      {/* Header */}
      <div className="flex items-center justify-between py-6 pr-4 pl-6">
        {!isCollapsed && <span className={`text-md font-medium tracking-[0.05em] ${colors.sidebarMenuLabel} uppercase`}>{t("sidebar.menu")}</span>}
        <button data-testid="app-sidebar-collapse-toggle" onClick={toggleSidebar} className={`p-1 ${colors.sidebarIconInactive} transition-colors ${colors.sidebarHoverTextPrimary}`}>
          {isCollapsed ? (
            <ChevronDoubleRightIcon className={`h-4 w-4 cursor-pointer ${colors.sidebarMenuLabel}`} />
          ) : (
            <ChevronDoubleLeftIcon className={`h-4 w-4 cursor-pointer ${colors.sidebarMenuLabel}`} />
          )}
        </button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 px-3 pb-4" data-testid="app-sidebar-nav">
        <ul className="space-y-1">
          {filteredMenuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <li key={item.id}>
                <div
                  data-testid={parentMenuTestId(item)}
                  className={`group flex cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${colors.sidebarHoverBg}`}
                  onClick={(e) => handleMenuClick(item, e)}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      {...(isCollapsed && {
                        "data-tooltip-id": `sidebar-tooltip-${item.id}`,
                        "data-tooltip-content": t(item.name, item.label),
                        "data-tooltip-place": "right"
                      })}
                    >
                      <IconComponent
                        size={20}
                        className={`flex-shrink-0 ${item.isActive ? colors.sidebarIconActive : colors.sidebarIconInactive}`}
                      />
                    </div>
                    {!isCollapsed && (
                      <span className={`text-sm font-medium ${item.isActive ? colors.sidebarTextActive : colors.sidebarTextInactive}`}>
                        {t(item.name, item.label)}
                      </span>
                    )}
                  </div>
                  {!isCollapsed && item.children && item.children.length > 0 && (
                    <div className={colors.sidebarIconInactive}>
                      {expandedSections[item.id] ? (
                        <ChevronUpIcon className={`h-4 w-4 ${item.isActive ? colors.sidebarIconActive : colors.sidebarTextInactive}`} />
                      ) : (
                        <ChevronDownIcon className={`h-4 w-4 ${item.isActive ? colors.sidebarIconActive : colors.sidebarTextInactive}`} />
                      )}
                    </div>
                  )}
                </div>
                {!isCollapsed && item.children && item.children.length > 0 && expandedSections[item.id] && (
                  <ul className="relative mt-1 ml-5 space-y-0.5">
                    {/* Vertical line aligned with parent icon */}
                    <div className={`absolute top-0 bottom-0 left-0 z-10 w-0.5 ${colors.sidebarSubmenuVerticalLine}`}></div>
                    {item.children.map((child, index) => (
                      <li key={index}>
                        <div
                          data-testid={pathToSidebarNavTestId(child.path)}
                          className={`relative cursor-pointer rounded-r-md py-1.5 pl-6 text-sm transition-colors ${
                            child.isActive
                              ? `${colors.sidebarSubmenuActiveBg} ${colors.sidebarSubmenuActiveText}`
                              : `${colors.sidebarIconInactive} ${colors.sidebarHoverBg} ${colors.sidebarHoverText}`
                          }`}
                          onClick={() => handleSubmenuClick(child)}
                        >
                          {child.isActive && (
                            <div className={`absolute top-0 bottom-0 left-0 z-10 w-1 ${colors.sidebarSubmenuActiveBorder}`}></div>
                          )}
                          {t(child.navName, child.name)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom section - Theme toggle and copyright */}
      {!isCollapsed && (
        <div className="space-y-4 px-4 pb-6">
          {/* Theme Toggle 
          <div className="flex items-center justify-center">
            <div className={`flex rounded-full border ${colors.sidebarToggleBorder} ${colors.sidebarToggleBg} p-0.5`}>
              <button
                onClick={() => setTheme("dark")}
                className={`flex items-center space-x-2 rounded-full px-4 py-1 text-sm font-medium transition-all duration-200 ${
                  isDark ? `${colors.sidebarToggleActiveBg} ${colors.sidebarToggleActiveText}` : `${colors.sidebarToggleInactiveText} ${colors.sidebarToggleHoverText}`
                }`}
              >
                <MoonIcon className="h-4 w-4" />
                <span>{t("sidebar.theme.dark")}</span>
              </button>
              <button
                // onClick={() => setTheme("light")}
                className={`flex items-center space-x-2 rounded-full px-4 py-1 text-sm font-medium transition-all duration-200 ${
                  !isDark ? `${colors.sidebarToggleLightButtonBg} ${colors.sidebarToggleLightButtonText}` : `${colors.sidebarToggleInactiveText} ${colors.sidebarToggleHoverText}`
                }`}
              >
                <SunIcon className="h-4 w-4" />
                <span>{t("sidebar.theme.light")}</span>
              </button>
            </div>
          </div>
          */}

          {/* Copyright */}
          <div className="text-center">
            <p className={`text-xs font-normal ${colors.sidebarCopyright}`}>{t("sidebar.copyright", { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      )}

      
      {/* Global Error Detail Modal */}
      <ErrorDetailsModal
        isOpen={open}
        onClose={() => setOpen(false)}
        errors={errors}
        showCloseButton={false}
      />

      {isCollapsed && filteredMenuItems.map((item) => (
        <Tooltip
          key={`tooltip-${item.id}`}
          id={`sidebar-tooltip-${item.id}`}
          place="right"
          className="!text-sm !rounded-lg !px-3 !py-2 !z-50 !shadow-lg"
          style={{
            backgroundColor: colors.sidebarTooltipBg,
            color: colors.sidebarTooltipText,
            fontSize: '14px',
            borderRadius: '8px',
            padding: '8px 12px',
            zIndex: 50,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        />
      ))}
    </div>
  );
};

export default Sidebar;
